/* Every date here is a calendar date in the HOTEL's timezone — how the
   server prices a stay (backend/services/nightlyRate.js, routes/api.js).
   Read in the browser's own timezone instead, a stored rate date (an instant)
   could land on the neighbouring day: a guest in London or Dubai was shown a
   boundary night at one price and charged another, and a guest west of UTC
   had every night priced a day early. */
const HOTEL_TZ = "Asia/Kolkata";
const hotelFmt = new Intl.DateTimeFormat("en-CA", { timeZone: HOTEL_TZ });
/** "YYYY-MM-DD" for a date string or a stored instant, in hotel time. */
export const hotelDateStr = d => hotelFmt.format(new Date(d));

// Guest-count-based room pricing, shared by the public booking form and the
// admin approve-booking suggestion — one formula, so the two never drift.
// pricePerNight covers up to baseOccupancy guests; each guest beyond that
// (rooms saved before this field existed fall back to `capacity`, which
// reproduces the old flat-price behavior exactly) adds extraPersonCharge
// per night.

// A night's base rate — whichever season has a dateRange covering that
// calendar date, else the room's flat pricePerNight. Mirrors
// backend/routes/api.js's effectiveNightlyRate (duplicated rather than
// shared, since frontend/backend run in separate JS runtimes) — keep both
// in sync if this logic ever changes.
export function effectiveNightlyRate(room, dateStr) {
  if (!room) return 0;
  for (const season of room.rateOverrides || []) {
    const covered = (season.dateRanges || []).some(r =>
      dateStr >= hotelDateStr(r.startDate) && dateStr <= hotelDateStr(r.endDate)
    );
    if (covered) return season.price;
  }
  return room.pricePerNight || 0;
}

// `dateStr` is optional — omit it for a date-agnostic "from ₹X/night" teaser
// figure; pass it for the true rate on a specific night.
export function pricePerNightForGuests(room, guestCount, dateStr) {
  if (!room) return 0;
  const base = dateStr ? effectiveNightlyRate(room, dateStr) : (room.pricePerNight || 0);
  const baseOccupancy = room.baseOccupancy || room.capacity || 1;
  const extraGuests = Math.max(0, (guestCount || 1) - baseOccupancy);
  return base + extraGuests * (room.extraPersonCharge || 0);
}

// Sums each individual night's rate rather than a flat per-night × nights
// multiply, since a stay can span nights with different override prices.
// A same-day (checkIn === checkOut, hourly-use) stay bills as a full
// night — mirrors how the backend normalizes it at booking time, so the
// price shown here always matches what actually gets charged.
export function calcRoomTotal(room, guestCount, checkIn, checkOut) {
  if (!room || !checkIn || !checkOut) return 0;
  // Nights walked as calendar dates (UTC arithmetic on the hotel's dates),
  // so no browser timezone can shift which nights are priced.
  const utc = v => { const [y, m, d] = hotelDateStr(v).split("-").map(Number); return Date.UTC(y, m - 1, d); };
  const start = utc(checkIn);
  let end = utc(checkOut);
  if (end <= start) end = start + 86400000;
  let total = 0;
  for (let t = start; t < end; t += 86400000) {
    total += pricePerNightForGuests(room, guestCount, new Date(t).toISOString().slice(0, 10));
  }
  return total;
}
